import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import {
  formatApproxLcoeAuthored,
  formatApproxLcoePerMwh,
  formatApproxLcoeRange,
} from '../../../../lib/lcoeDisplay';
import { LcoeProvenanceFootnote } from '../../report/LcoeProvenanceFootnote';
import { ReportSourceNote } from '../../report/ReportSourceNote';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import {
  EditorialDataTable,
  EditorialFigureCaption,
  EditorialStackBar,
} from '../../report/editorial';
import {
  correctedLcoeForBucket,
  resolveReportCorrectedLcoe,
  technologyLabelToBucket,
} from '../../../../lib/finDecision/reportLcoeSurface';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

/** True when an authored LCOE string carries no usable computed value. */
function isPendingLcoe(lcoeResult: string | undefined | null): boolean {
  const t = (lcoeResult ?? '').trim();
  if (t === '' || t === '—') return true;
  if (/^pending$/i.test(t)) return true;
  // No digit at all ⇒ not a usable figure (e.g. "TBD", "n/a").
  return !/\d/.test(t);
}

/**
 * Parse an authored LCOE-COMPONENT cost string to a $/MWh number, or null
 * when the string is not safely on the $/MWh basis. Unit-guarded so the D2
 * cost stack never mixes bases:
 *   - accepts "$12", "12", "$12/MWh", "$20–30/MWh" (range → midpoint);
 *   - REJECTS an explicit non-/MWh unit ("$95/kW-yr", "$5.5M/MW") — a
 *     different basis must never join a proportional $/MWh stack;
 *   - REJECTS magnitude suffixes (k/M/B[n]) — those are capital figures,
 *     not per-MWh rates;
 *   - sanity-bounds the result to (0, 1000) $/MWh.
 * No value is fabricated — an unparseable component simply drops out.
 */
function parsePerMwhComponent(raw: string | undefined | null): number | null {
  const t = (raw ?? '').trim();
  if (!t || !/\d/.test(t)) return null;
  // Explicit slash unit must be /MWh (allow "$/MWh" notation too).
  const slashUnit = t.match(/\/\s*([A-Za-z][A-Za-z-]*)/);
  if (slashUnit && slashUnit[1]!.toLowerCase() !== 'mwh') return null;
  // Magnitude suffix ⇒ capital-scale figure, not a per-MWh rate.
  if (/\d\s*(k|m|b|bn|million|billion)\b/i.test(t)) return null;
  const nums = (t.replace(/,/g, '').match(/\d+(?:\.\d+)?/g) ?? []).map(Number);
  if (nums.length === 0) return null;
  // Range "20–30" → midpoint; single figure otherwise.
  const value = nums.length >= 2 ? (nums[0]! + nums[1]!) / 2 : nums[0]!;
  if (!Number.isFinite(value) || value <= 0 || value >= 1000) return null;
  return value;
}

interface CostStackRow {
  technology: string;
  lcoeLabel: string;
  segments: Array<{ label: string; value: number; color: string; darkLabel?: boolean }>;
}

export const LcoeBreakdownSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const rows = report.lcoeDetailedAnalysis ?? [];
  const correctedSurface = resolveReportCorrectedLcoe(report);

  const lcoeCell = (i: number): string => {
    const row = rows[i]!;
    if (!isPendingLcoe(row.lcoeResult)) {
      return formatApproxLcoeAuthored(row.lcoeResult);
    }
    const bucket = technologyLabelToBucket(row.technology);
    const entry = correctedLcoeForBucket(correctedSurface, bucket);
    if (entry.midUsdPerMwh != null) {
      const low = Math.round(entry.lowUsdPerMwh ?? entry.midUsdPerMwh);
      const high = Math.round(entry.highUsdPerMwh ?? entry.midUsdPerMwh);
      return low < high
        ? formatApproxLcoeRange(low, high)
        : formatApproxLcoePerMwh(entry.midUsdPerMwh);
    }
    return 'pending';
  };
  const hasPendingRows = rows.some((_, i) => lcoeCell(i) === 'pending');

  // ── D2 cost stack — per-technology LCOE component composition ──────
  // Built ONLY from the authored $/MWh components (Fixed O&M / Var O&M /
  // Fuel via the unit-guarded parser above). A technology renders a stack
  // row only when ≥2 components parse, so a one-segment 100% bar never
  // fabricates a "split".
  // GAP: `capitalCostPerMW` is a $/MW CAPITAL figure, not a per-MWh LCOE
  // contribution — the amortised capital-recovery component (and carbon)
  // is NOT generated. Do NOT derive it from the LCOE remainder; the table
  // still carries CAPEX/MW and the LCOE column carries the all-in figure.
  const stackPalette = [
    { color: roleColor(theme, 'accent'), darkLabel: false },
    { color: roleColorAlpha(theme, 'accent', 0.65), darkLabel: false },
    { color: roleColorAlpha(theme, 'accent', 0.35), darkLabel: true },
  ] as const;
  const stackRows: CostStackRow[] = rows.flatMap((row, i) => {
    const components = [
      { label: 'Fixed O&M', value: parsePerMwhComponent(row.fixedOM) },
      { label: 'Var O&M', value: parsePerMwhComponent(row.variableOM) },
      { label: 'Fuel', value: parsePerMwhComponent(row.fuelCost) },
    ].filter((c): c is { label: string; value: number } => c.value != null);
    if (components.length < 2) return [];
    return [{
      technology: row.technology,
      lcoeLabel: lcoeCell(i),
      segments: components.map((c, j) => ({
        label: `${c.label} $${c.value}`,
        value: c.value,
        color: stackPalette[j % stackPalette.length]!.color,
        darkLabel: stackPalette[j % stackPalette.length]!.darkLabel,
      })),
    }];
  });

  const stackHeadStyle: React.CSSProperties = {
    fontFamily: MONO,
    fontSize: REPORT_TEXT_PX.caption,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: roleColor(theme, 'muted'),
    fontVariantNumeric: 'tabular-nums',
  };

  return (
    <SectionShell label={label} hasData={rows.length > 0} report={report}>
      {/* data-span-columns: defensive — lets the wide table span both flow
          columns if this section's supportsColumnLayout is ever flipped on.
          Carrier div holds the testid (EditorialDataTable has no testid prop). */}
      <div data-span-columns className="overflow-x-auto" data-testid="lcoe-breakdown-table">
        <EditorialDataTable
          theme={theme}
          // golden-pass 2026-06-27 — size columns to CONTENT so the verbose CF
          // cell + the 7-figure CAPEX/MW value don't shred mid-token under the
          // forced table-layout:fixed. Companion to the deck-CSS override in
          // PaginatedReportView (this covers the continuous / Cmd-P view==PDF path).
          style={{ tableLayout: 'auto' }}
          columns={[
            // FIX-A — reserve room for the single-word "Technology" header +
            // the all-in "≈$60/MWh" LCOE value so a `table-layout:fixed`
            // equal-split (7 cols ≈ 14% each) no longer crushes them into a
            // mid-word break ("TECHNOL/OGY", "≈$60/MW\nh"). `headerNoWrap` keeps
            // the header label on one line; the explicit widths the fixed table
            // honours give the two widest columns the room they need, and the
            // narrow numeric columns (CF, Fuel) absorb the remainder.
            { header: 'Technology', headerNoWrap: true, width: '18%' },
            { header: 'CAPEX/MW', numeric: true, headerNoWrap: true, width: '15%' },
            { header: <>Fixed O&amp;M</>, numeric: true, width: '13%' },
            { header: <>Var O&amp;M</>, numeric: true, width: '12%' },
            { header: 'Fuel', numeric: true, width: '11%' },
            { header: 'CF', numeric: true, width: '8%' },
            { header: 'LCOE', numeric: true, headerNoWrap: true, width: '15%' },
          ]}
          rows={rows.map((row, i) => [
            row.technology,
            row.capitalCostPerMW,
            row.fixedOM,
            row.variableOM,
            row.fuelCost,
            row.capacityFactor,
            // Headline figure keeps the strong ink emphasis the old table had.
            <span style={{ color: roleColor(theme, 'ink'), fontWeight: 600 }}>{lcoeCell(i)}</span>,
          ])}
        />
      </div>

      {/* D2 cost stack (per-technology $/MWh component composition). Renders
          only when the authored components pass the unit guard — degrades to
          the table alone otherwise (never a fabricated split). */}
      {stackRows.length > 0 && (
        <div className="mt-4" data-testid="lcoe-breakdown-cost-stack" data-span-columns>
          {stackRows.map(sr => (
            <div key={sr.technology} className="mb-3">
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <span style={{ ...stackHeadStyle, color: roleColor(theme, 'ink') }}>{sr.technology}</span>
                <span style={stackHeadStyle}>{sr.lcoeLabel}</span>
              </div>
              <EditorialStackBar
                theme={theme}
                height={26}
                segments={sr.segments}
                legend={sr.segments.map(s => ({ color: s.color, label: s.label }))}
              />
            </div>
          ))}
          {/* Redhead wave 3 (defect 5a) — report-global sequential numbering via
              the figure counter (was a hardcoded 1). */}
          <EditorialFigureCaption theme={theme} figureKey="lcoeBreakdown:costStack" prefix="Figure ">
            Levelised-cost composition by technology – fixed O&amp;M, variable
            O&amp;M and fuel components as authored ($/MWh), normalised to the
            bar. Capital recovery is carried in the LCOE column; a per-MWh
            capital-recovery component is not yet computed.
          </EditorialFigureCaption>
        </div>
      )}

      {hasPendingRows && (
        <ReportSourceNote className="mt-1" testId="lcoe-breakdown-industry-norm-footnote">
          Pending rows have no verified site-specific LCOE (no unsupported midpoint average is substituted). Published primary-source comparison references — EPRI 2024 Report 3002029428 (2023 unsubsidized SMR FOAK $111–$143/MWh vs. projected 2035 NOAK $94–$121/MWh, 2023$ real) and Lazard LCOE+ v17.0 (June 2024: unsubsidized CCGT $45–$108/MWh, utility solar + storage $60–$210/MWh, onshore wind + storage $45–$133/MWh, 2024$ nominal) — are comparison references only, not site estimates or interchangeable 24/7 firmed-load costs. See Appendix &amp; References.
        </ReportSourceNote>
      )}
      <LcoeProvenanceFootnote report={report} />
    </SectionShell>
  );
};
